import React, { useEffect, useRef, useState } from 'react'
import Modal from './Modal'
import Button from '../ui/buttons/Button'
import Input from '../ui/inputs/Input'
import Select from '../ui/selects/Select'
import { Box, X } from 'lucide-react'
import { fetchWithCsrf } from '../../utils/fetchWithCsrf'
import { snakecaseKeys } from '../../utils/snakecaseKeys'
import { camelizeKeys } from '../../utils/camelizeKeys'
import CepInput from '../ui/inputs/CepInput'
import CpfInput from '../ui/inputs/CpfInput'
import { useToast } from '../ui/toast/ToastContext'

const EditPlanModal = ({ onClose, clientName, clientId, onSuccess }) => {
  const [form, setForm] = useState({
    paymentType: '',
    companionId: '',
    paymentForm: '',
    reviewerId: '',
    cpfCnpj: '',
    zipcode: '',
    address: '',
    number: '',
    complement: '',
    neighbourhood: '',
    city: '',
    state: '',
    origin: '',
    officeOrigin: ''
  })
  const [paymentTypeOptions, setPaymentTypeOptions] = useState([])
  const [companionOptions, setCompanionOptions] = useState([])
  const [paymentFormOptions, setPaymentFormOptions] = useState([])
  const [reviewerOptions, setReviewerOptions] = useState([])
  const [originOptions, setOriginOptions] = useState([])
  const [officeOriginOptions, setOfficeOriginOptions] = useState([])
  const [isSaving, setIsSaving] = useState(false)
  const [hasPlan, setHasPlan] = useState(false)
  const { addToast } = useToast()

  const cepRunCount = useRef(0)

  useEffect(() => {
    fetch(`/app/planner/plans?client_id=${clientId}`)
      .then(res => res.json())
      .then(data => {
        const camelData = camelizeKeys(data)

        setHasPlan(!!camelData.paymentType)
        // setForm({
        //   paymentType: camelData.paymentType || '',
        //   companionId: camelData.companionId || '',
        //   paymentForm: camelData.paymentForm || '',
        //   reviewerId: camelData.reviewerId || '',
        //   cpfCnpj: camelData.cpfCnpj || '',
        //   zipcode: camelData.zipcode || '',
        //   address: camelData.address || '',
        //   neighbourhood: camelData.neighbourhood || '',
        //   state: camelData.state || '',
        //   city: camelData.city || '',
        //   number: camelData.number || '',
        //   complement: camelData.complement || '',
        //   origin: camelData.origin || '',
        //   officeOrigin: camelData.officeOrigin || ''
        // })
        setForm(camelData)
      })
      .catch(err => {
        console.error('Erro ao buscar dados do plano:', err)
        addToast({ type: 'error', message: 'Erro ao carregar plano do cliente.' })
      })
  }, [clientId])

  useEffect(() => {
    fetch('/app/shared/form_options')
      .then(res => res.json())
      .then(data => {
        const camelData = camelizeKeys(data)

        const paymentTypes = camelData.paymentTypes.map(p => ({ label: p.label, value: p.value }))
        const paymentForms = camelData.paymentForms.map(p => ({ label: p.label, value: p.value }))
        const reviewers = camelData.reviewers.map(p => ({ label: p.label, value: p.value }))
        const companions = camelData.companions.map(c => ({ label: c.label, value: c.value }))
        const origins = camelData.origins.map(c => ({ label: c.label, value: c.value }))
        const officeOrigins = camelData.officeOrigins.map(c => ({ label: c.label, value: c.value }))

        setPaymentTypeOptions(paymentTypes)
        setPaymentFormOptions(paymentForms)
        setReviewerOptions(reviewers)
        setCompanionOptions(companions)
        setOriginOptions(origins)
        setOfficeOriginOptions(officeOrigins)
      })
      .catch(err => {
        console.error('Erro ao carregar opções do formulário', err)
        addToast({ type: 'error', message: 'Erro ao carregar opções do formulário.' })
      })
  }, [])

  useEffect(() => {
    const cep = form.zipcode?.replace(/\D/g, '')

    cepRunCount.current += 1;
  
    // Pula as duas primeiras execuções
    if (cepRunCount.current <= 2) return;

    if (cep?.length === 8) {
      fetch(`https://viacep.com.br/ws/${cep}/json/`)
        .then(res => res.json())
        .then(data => {
          if (data.erro) return
          setForm(prev => ({
            ...prev,
            address: data.logradouro || '',
            neighbourhood: data.bairro || '',
            city: data.localidade || '',
            state: data.uf || ''
          }))
        })
        .catch(err => {
          console.error('[CEP EFFECT] Erro ao buscar endereço por CEP:', err)
        })
    }
  }, [form.zipcode])

  // const handleChange = (e) => {
  //   setForm({ ...form, [e.target.name]: e.target.value })
  // }

  const handleChange = (e) => {
    const { name, value } = e.target
  
    if (name === 'paymentType') {
      if (value === 'spouse_plan') {
        setForm(prev => ({
          ...prev,
          paymentType: value,
          paymentForm: ''
        }))
      } else {
        setForm(prev => ({
          ...prev,
          paymentType: value
        }))
      }
    } else {
      setForm(prev => ({
        ...prev,
        [name]: value
      }))
    }
  }  

  const handleSubmit = (e) => {
    e.preventDefault()
    setIsSaving(true)

    const payload = snakecaseKeys({ plan: form })

    fetchWithCsrf(`/app/planner/plans?client_id=${clientId}`, {
      method: 'PUT',
      body: JSON.stringify(payload)
    })
      .then(async (res) => {
        const json = await res.json()
        if (!res.ok) throw new Error(json.errors || 'Erro ao salvar plano.')

        addToast({
          type: 'success',
          message: hasPlan ? 'Plano atualizado com sucesso!' : 'Plano cadastrado com sucesso!'
        })

        onSuccess()
      })
      .catch(err => {
        console.error('Erro ao salvar plano:', err)
        addToast({ type: 'error', message: err.message })
      })
      .finally(() => setIsSaving(false))
  }

  return (
    <Modal onClose={onClose}>
      <div className="p-10">
        <div className="mb-6 flex items-center justify-between">
          <div className="flex items-center gap-3 text-blueDarker font-jost text-2xl font-semibold">
            <Box size={24} className="text-tealMedium" strokeWidth={1.25} />
            {hasPlan ? 'Alterar Plano' : 'Cadastrar Plano'}
          </div>
          <button onClick={onClose}>
            <X size={24} className="text-tealBase hover:opacity-80" strokeWidth={1.25} />
          </button>
        </div>

        <div className="border-t border-b border-bluePale py-4 mb-6">
          <p className="font-jost text-lg text-blueDarker">
            <span className="font-semibold">Cliente:</span> {clientName}
          </p>
        </div>

        <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-4 font-jost text-sm">
          <Select required disabled={isSaving} name="paymentType" label="Plano" value={form.paymentType} onChange={handleChange} options={paymentTypeOptions} />
          <Select required disabled={isSaving} name="companionId" label="Treinador" value={form.companionId} onChange={handleChange} options={companionOptions} />
          <Select
            required={form.paymentType !== 'spouse_plan'}
            disabled={isSaving || form.paymentType === 'spouse_plan'}
            name="paymentForm"
            label="Forma de pagamento"
            value={form.paymentForm}
            onChange={handleChange}
            options={paymentFormOptions}
          />
          <Select required disabled={isSaving} name="reviewerId" label="Revisor" value={form.reviewerId} onChange={handleChange} options={reviewerOptions} />
          <Select required disabled={isSaving} name="origin" label="Origem" value={form.origin} onChange={handleChange} options={originOptions} />
          <Select required disabled={isSaving} name="officeOrigin" label="Escritório" value={form.officeOrigin} onChange={handleChange} options={officeOriginOptions} />

          <CpfInput required disabled={isSaving} name="cpfCnpj" label="CPF" value={form.cpfCnpj} onChange={handleChange} />
          <CepInput required disabled={isSaving} name="zipcode" label="CEP" value={form.zipcode} onChange={handleChange} />
          <Input required disabled={isSaving} name="address" label="Endereço" value={form.address} onChange={handleChange} />
          <Input required disabled={isSaving} name="neighbourhood" label="Bairro" value={form.neighbourhood} onChange={handleChange} />
          <Input required disabled={isSaving} name="state" label="Estado" value={form.state} onChange={handleChange} />
          <Input required disabled={isSaving} name="city" label="Cidade" value={form.city} onChange={handleChange} />
          <Input required disabled={isSaving} name="number" label="Número" value={form.number} onChange={handleChange} />
          <Input required disabled={isSaving} name="complement" label="Complemento" value={form.complement} onChange={handleChange} />

          <div className="md:col-span-2 flex justify-end mt-1">
            <Button type="submit" loading={isSaving}>
              {hasPlan ? 'SALVAR ALTERAÇÃO' : 'CADASTRAR PLANO'}
            </Button>
          </div>
        </form>
      </div>
    </Modal>
  )
}

export default EditPlanModal
